import { ref, shallowRef, computed, watch } from 'vue'
import { MapLayerMVTRenderer } from 'ifield-core-components'
import _ from 'lodash'
import store from '@/store'
import { MAP_LAYER_REFERENCE_TABLE_TYPE } from '@/constants.js'

/**
 * 対象キーのフィルタをパラメータに設定する
 * @param {Object} param 設定先のパラメータ
 * @param {String} key 対象キー
 * @param {Set} ids フィルタ対象IDのSet
 */
const setFilter = (param, key, ids) => {
  const filterParam = { [key]: ids }
  if (param.filter) {
    param.filter = { ...param.filter, ...filterParam }
  } else {
    param.filter = filterParam
  }
}

/**
 * 対象キーのフィルタをパラメータから取り除く
 * @param {Object} param 設定先のパラメータ
 * @param {String} key 対象キー
 */
const removeFilter = (param, key) => {
  if (!param.filter) return

  const keys = Object.keys(param.filter)
  if (keys.length === 1 && keys[0] === key) {
    param.filter = null
  } else {
    delete param.filter.place_id
    param.filter = { ...param.filter }
  }
}

export function useMapLayerFilter() {
  /**
   * 表示対象ID
   * @type {Ref.<Object>}
   */
  const targetIds = ref({
    indoorSite: null,
    indoorBuilding: null,
    place: null,
  })
  /**
   * コピーしたグローバルのレイヤー描画パラメータ
   * 元のグローバルパラメータに変更が無い限り使いまわして copy コストを抑えるために使用
   * @type {shallowRef.<Object[]>}
   */
  const layerRenderParametersGlobalClone = shallowRef([])

  /**
   * 対象IDをSet化したもの
   * @type {ComputedRef.<Object[]>}
   */
  const targetIdSet = computed(() => {
    const { indoorSite, indoorBuilding, place } = targetIds.value
    return {
      indoorSite: indoorSite ? new Set(indoorSite) : null,
      indoorBuilding: indoorBuilding ? new Set(indoorBuilding) : null,
      place: place ? new Set(place) : null,
    }
  })
  /**
   * マップレイヤーのマスターデータ
   */
  const mapLayers = computed(() => store.getters['mapLayer/mapLayers'])
  /**
   * Vuex で管理しているグローバルのレイヤー描画パラメータ
   * @type {ComputedRef.<Object[]>}
   */
  const layerRenderParametersGlobal = computed(
    () => store.getters['map/layerRenderParameters']
  )
  /**
   * フィルタを設定したレイヤー描画パラメータ
   * @type {ComputedRef.<Object[]>}
   */
  const layerRenderParameters = computed(() => {
    const params = [...layerRenderParametersGlobalClone.value]

    for (const param of params) {
      if (param.id !== MapLayerMVTRenderer.layerId) continue
      for (const subParam of param.subLayers) {
        const layer = mapLayers.value.find((l) => l.id === +subParam.id)
        if (!layer) continue

        if (
          layer.reference_table_type == MAP_LAYER_REFERENCE_TABLE_TYPE.PLACE
        ) {
          applyPlaceFilter(subParam)
        } else if (
          layer.reference_table_type ==
          MAP_LAYER_REFERENCE_TABLE_TYPE.INDOOR_SITE
        ) {
          applyIndoorSiteFilter(subParam)
        } else if (
          layer.reference_table_type ==
          MAP_LAYER_REFERENCE_TABLE_TYPE.INDOOR_BUILDING
        ) {
          applyIndoorBuildingFilter(subParam)
        }
      }
    }

    return params
  })

  /**
   * プレイスの地物フィルターを設定する
   * @param {Object} 対象レイヤーのパラメータ
   */
  const applyPlaceFilter = (param) => {
    if (targetIdSet.value.place) {
      setFilter(param, 'place_id', targetIdSet.value.place)
    } else {
      removeFilter(param, 'place_id')
    }
  }

  /**
   * サイトの地物フィルターを設定する
   * @param {Object} 対象レイヤーのパラメータ
   */
  const applyIndoorSiteFilter = (param) => {
    if (targetIdSet.value.indoorSite) {
      setFilter(param, 'indoor_site_id', targetIdSet.value.indoorSite)
    } else {
      removeFilter(param, 'indoor_site_id')
    }
  }

  /**
   * ビルの地物フィルターを設定する
   * @param {Object} 対象レイヤーのパラメータ
   */
  const applyIndoorBuildingFilter = (param) => {
    if (targetIdSet.value.indoorBuilding) {
      setFilter(param, 'indoor_building_id', targetIdSet.value.indoorBuilding)
    } else {
      removeFilter(param, 'indoor_building_id')
    }
  }

  /**
   * targetIds を更新する
   * @param {Object} value 更新値
   */
  const updateTargetIds = (value) => {
    if (!value) return
    for (const [k, v] of Object.entries(value)) {
      if (k in targetIds.value) {
        targetIds.value[k] = v
      }
    }
  }

  // グローバルパラメータ変更時に deep copy を取る
  watch(layerRenderParametersGlobal, (value) => {
    layerRenderParametersGlobalClone.value = _.cloneDeep(value)
  })

  return { targetIds, updateTargetIds, layerRenderParameters }
}
